import React, { useState } from 'react';
import { Tabs } from '@nexus/lib-react/dist/core/Tabs/Tabs';

import { Headline6, Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import { useI18n } from '../../../../i18n';
import S from './style/Dac7TabContent';
import V from './utils/validation';
import { SellerPrivateDac7 } from '../../../../../typings/dac7';
import Dac7Input from './components/Dac7Input';
import Dac7Dropdown from './components/Dac7Dropdown';
import countries from './utils/countriesList';
import Dac7DateField from './components/Dac7DateField';
import Dac7Addresses from './components/Dac7Addresses';
import Dac7FinancialDetails from './components/Dac7FinancialDetails';
import Dac7IdentificationDetails from './components/Dac7IdentificationDetails';

interface Props {
  data?: Readonly<SellerPrivateDac7>;
  isDesktop: boolean;
}

type TabType = 'pesel' | 'country_birth';

const DAC7TabContentPrivate = ({ data, isDesktop }: Props) => {
  const [tabType, setTabType] = useState<TabType>('pesel');
  const i18n = useI18n();
  const isDisabled = !!data.submittedAt;

  const identificationTabOptions: {
    id: TabType;
    content: JSX.Element;
  }[] = [
    {
      id: 'pesel',
      content: <Paragraph4>{i18n('dlv_dac7_country_pesel_tab')}</Paragraph4>,
    },
    {
      id: 'country_birth',
      content: <Paragraph4>{i18n('dlv_dac7_country_birth_tab')}</Paragraph4>,
    },
  ];

  const tabTypeHandler = (value: TabType) => {
    setTabType(value);
  };

  return (
    <>
      <S.Dac7TabContentWrapper>
        <S.Dac7Fieldset className="dac7-personal-details">
          <legend>
            <Headline6 mb={12}>{i18n('dlv_dac7_privateDetails')}</Headline6>
          </legend>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_firstName"
              label={i18n('dlv_dac7_firstName')}
              pattern={V.getMinCharsPattern(2)}
              required
              type="text"
              value={data.firstName || ''}
              disabled={!!data.firstName}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_lastName"
              label={i18n('dlv_dac7_lastName')}
              pattern={V.getMinCharsPattern(2)}
              required
              type="text"
              value={data.lastName || ''}
              disabled={!!data.lastName}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7DateField
              name="dac7_birthDate"
              label={i18n('dlv_dac7_birthDate')}
              value={data.birthDate || ''}
              disabled={!!data.birthDate}
              required
            />
          </S.Dac7FormField>
        </S.Dac7Fieldset>
        <S.StyledRequiredFieldsText>
          {i18n(`dlv_dac7_required_fields_description`)}
        </S.StyledRequiredFieldsText>
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={12} ml={isDesktop ? 0 : 12} mr={isDesktop ? 0 : 12}>
            {i18n('dlv_dac7_addresses')}
          </Headline6>
        </legend>
        <Dac7Addresses addresses={data.addresses} />
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={12} ml={isDesktop ? 0 : 12} mr={isDesktop ? 0 : 12}>
            {i18n('dlv_dac7_identificationDetails')}
          </Headline6>
        </legend>
        {isDisabled ? null : (
          <Paragraph4 mt={8} mb={24} ml={isDesktop ? 0 : 12} mr={isDesktop ? 0 : 12}>
            {i18n('dlv_dac7_add_identification_details_description')}
          </Paragraph4>
        )}
        <Tabs options={identificationTabOptions} onSelect={tabTypeHandler} selectedId={tabType} />

        {tabType === 'pesel' ? (
          <Dac7IdentificationDetails identificationDetails={data.identificationDetails} />
        ) : (
          <S.Dac7Fieldset className="dac7-identification-details-country-birth">
            <S.Dac7FormField>
              <Dac7Dropdown
                sourceItems={countries}
                initialSelectedIds={[data.birthCountry]}
                name="dac7_birthCountry"
                label={i18n('dlv_dac7_birthCountry')}
                placeholder={i18n('dlv_dac7_birthCountryPlaceholder')}
                disabled={!!data.birthCountry}
              />
            </S.Dac7FormField>
          </S.Dac7Fieldset>
        )}
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={12} ml={isDesktop ? 0 : 12} mr={isDesktop ? 0 : 12}>
            {i18n('dlv_dac7_financialDetails')}
          </Headline6>
        </legend>
        <Dac7FinancialDetails financialDetails={data.financialDetails} />
      </S.Dac7TabContentWrapper>
    </>
  );
};

export default DAC7TabContentPrivate;
